import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Image, ImageBackground, FlatList, Linking, SafeAreaView, ScrollView } from 'react-native';
import { useStore } from 'Store';
import { t } from 'Locale/index';
import { Icon, Button, Hr } from 'LocalGluons/index';

import { Device } from 'DeviceUtils';
import {
    ConsentsToSaveRequest,
    ConsentToSave
} from '@24i/st-api-connector/interface/auth/requests';
import { CachedImage } from 'LocalGluons/components/CachedImage';
// eslint-disable-next-line import/no-unresolved
import { ConsentDefinition } from 'types';
import LinearGradient from 'react-native-linear-gradient';
import RedirectScene from '../../../runtime/Page/RedirectScene';
import { possibleApps } from '../../../runtime/Page/RedirectScene/getVoyoStoreParameters';
import portraitStyles, { landscapeStyles } from './VoyoConsentDialog.jss';
import VoyoConsentItem from './VoyoConsentItem/VoyoConsentItem';
import { renderLinks } from '../ParseUrlString/parseURLString';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const APP_PROMO_IMAGE = require('../../../../platformAssets/runtime/images/Apps.png');

interface VoyoConsentDialogProps {
    close: () => void;
    backgroundImageUrl: string;
    title: string;
    titleDescription: string;
    bottomDescription: string;
    isLandscape: boolean;
    logoUrl?: string;
    continueButtonText: string;
    consentDefinitions: ConsentDefinition[];
    itemId: number;
    setModalContent: (modalContent: unknown) => void;
}

const VoyoConsentDialog = ({
    close,
    backgroundImageUrl,
    title,
    titleDescription,
    bottomDescription,
    isLandscape,
    logoUrl,
    continueButtonText,
    consentDefinitions,
    itemId,
    setModalContent
}: VoyoConsentDialogProps): JSX.Element => {
    const { appManager } = useStore();
    const [selectedIds, setSelectedIds] = useState<Array<number>>([]);
    const selectedIdsRef = useRef(selectedIds);
    selectedIdsRef.current = selectedIds;

    const [isButtonEnabled, setButtonEnabled] = useState<boolean>(false);
    const {
        backstageConnector: { auth, vod }
    } = appManager.getApi();

    const closeModalCallback = () => {
        setModalContent(null);
    };

    const onSelectedPress = (order: number) => {
        setSelectedIds((currentSelectedIds: Array<number>) => {
            const newSelectedIds = currentSelectedIds;
            if (newSelectedIds.includes(order)) {
                const index = newSelectedIds.indexOf(order);
                if (index !== -1) {
                    newSelectedIds.splice(index, 1);
                }
            } else {
                newSelectedIds.push(order);
            }
            return [...newSelectedIds];
        });
    };

    // TODO: I used logic from NotificagionDialog refactore to get rid of isLandscape with this on both places
    const styles = isLandscape ? landscapeStyles : portraitStyles;
    const buttonStyle = { button: styles.button };
    const buttonTabletStyle = { button: landscapeStyles.buttonContinue };
    const buttonCancelTabletStyle = { button: landscapeStyles.buttonCancel };
    const launchMarketing = t('PARTNER.VOYO.LAUNCH_APP_MARKETING_OPTOUT_MOBILES');

    const onContinuePress = async () => {
        const consentsToSaveRequest: ConsentsToSaveRequest = {
            consents: consentDefinitions.map((item) => {
                const consentToSave: ConsentToSave = {
                    name: item.name,
                    value: selectedIds.includes(item.order)
                };
                return consentToSave;
            })
        };

        try {
            // TODO fix typo in saveConsetns in connector and than back in code
            await auth.saveConsents('VOYO', consentsToSaveRequest);
        } catch (err) {
            console.log(err);
        }
        const { deeplinkUrl, thirdPartyAppId } = await vod.getSmallscreenDeeplink('VOYO', itemId);

        if (Device.isWeb) {
            Linking.openURL(deeplinkUrl);
        }
        if (!Device.isWeb && thirdPartyAppId && possibleApps.includes(thirdPartyAppId)) {
            const deeplinkMobileUrl = deeplinkUrl.replace('https', thirdPartyAppId);
            Linking.openURL(deeplinkMobileUrl).catch(() =>
                setModalContent(
                    <RedirectScene
                        close={closeModalCallback}
                        thirdPartyApp={thirdPartyAppId}
                        deeplinkUrl={deeplinkUrl}
                    />
                )
            );
        } else {
            Linking.openURL(deeplinkUrl);
        }
        close();
    };

    useEffect(() => {
        const isClickAllowed = consentDefinitions.every((item) => {
            if (item.mandatory && !selectedIds.includes(item.order)) {
                return false;
            }
            return true;
        });
        setButtonEnabled(isClickAllowed);
    }, [selectedIds, setButtonEnabled, consentDefinitions]);

    return (
        <ImageBackground source={{ uri: backgroundImageUrl }} style={styles.backgroundImage}>
            <SafeAreaView style={styles.safeAreaContainer}>
                {Device.isWeb && <Text style={styles.platformWeb}>VOYO</Text>}
                {Device.isPhone && (
                    <View style={styles.scrollViewContainer}>
                        <ScrollView contentContainerStyle={styles.contentScrollview}>
                            <View style={styles.windowMobile}>
                                <View style={styles.headerContainer}>
                                    <View style={styles.backIconContainer}>
                                        <Icon
                                            style={styles.backIcon}
                                            name="arrow_left"
                                            key="arrow_left"
                                            testID="back_button"
                                            onPress={close}
                                        />
                                    </View>
                                    <Text style={styles.title}>VOYO</Text>
                                </View>
                                <View style={styles.iconBox}>
                                    {logoUrl && (
                                        <CachedImage
                                            source={{ uri: logoUrl }}
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            style={styles.logo}
                                        />
                                    )}
                                    <Text style={styles.subTitle}>{title}</Text>
                                </View>

                                <View>
                                    <Text style={styles.greyText}>{titleDescription}</Text>
                                </View>
                                <Hr style={{ marginTop: 12 }} />
                                <FlatList
                                    data={consentDefinitions}
                                    keyExtractor={(item) => item.name}
                                    extraData={selectedIds}
                                    scrollEnabled={false}
                                    renderItem={({ item }) => (
                                        <VoyoConsentItem
                                            item={item}
                                            isLandscape={false}
                                            isSelected={selectedIds.includes(item.order)}
                                            onSelectedPress={onSelectedPress}
                                        />
                                    )}
                                />
                                <View style={styles.descriptionBox}>
                                    <Icon name="info_circle" style={[styles.infoIcon] as any} />
                                    <Image source={APP_PROMO_IMAGE} />
                                    <Text style={styles.greyText}>
                                        {renderLinks(bottomDescription)}
                                    </Text>
                                </View>
                                <Hr style={styles.lineMarginTop} />
                                <View style={styles.infoContainer}>
                                    <Text style={styles.launchMarketing}>
                                        {renderLinks(launchMarketing)}
                                    </Text>
                                </View>
                            </View>
                        </ScrollView>
                        <View style={styles.buttonContainer}>
                            <Button
                                style={buttonStyle}
                                title={t('PARTNER.VOYO.CLOSE_BTN')}
                                isSecondary
                                onPress={close}
                            />
                            <Button
                                style={buttonStyle}
                                title={continueButtonText}
                                onPress={onContinuePress}
                                isDisabled={!isButtonEnabled}
                                isSecondary={!isButtonEnabled}
                            />
                            <View style={styles.gradientContainer} pointerEvents="none">
                                <LinearGradient
                                    style={styles.gradient}
                                    colors={['#00000000', '#000000FF']}
                                />
                            </View>
                        </View>
                    </View>
                )}

                {Device.isTabletOrWeb && (
                    <ScrollView
                        style={styles.scrollViewContainer}
                        contentContainerStyle={styles.contentScrollview}
                    >
                        {Device.isTablet && <Text style={styles.platformTablet}>VOYO</Text>}
                        <View style={landscapeStyles.windowTablet}>
                            <View style={landscapeStyles.headerContainer}>
                                <View style={landscapeStyles.logoContainer}>
                                    {logoUrl && (
                                        <CachedImage
                                            source={{ uri: logoUrl }}
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            style={landscapeStyles.logo}
                                        />
                                    )}
                                </View>
                                <View style={landscapeStyles.headerTextContainer}>
                                    <Text style={landscapeStyles.title}>{title}</Text>
                                    <Text style={landscapeStyles.subTitle}>{titleDescription}</Text>
                                </View>
                            </View>
                            <Hr />
                            {consentDefinitions.map((item) => (
                                <VoyoConsentItem
                                    item={item}
                                    key={item.name}
                                    isLandscape={false}
                                    isSelected={selectedIds.includes(item.order)}
                                    onSelectedPress={onSelectedPress}
                                />
                            ))}

                            <View style={landscapeStyles.infoContainer}>
                                <Icon name="info_circle" style={landscapeStyles.infoIcon} />
                                <Text style={landscapeStyles.greyText}>
                                    {renderLinks(bottomDescription)}
                                </Text>
                            </View>
                            <Hr style={landscapeStyles.lineMarginTop} />
                            <View style={landscapeStyles.infoContainer}>
                                <Text style={styles.launchMarketing}>
                                    {renderLinks(launchMarketing)}
                                </Text>
                            </View>

                            <View style={landscapeStyles.buttonContainer}>
                                <Button
                                    style={buttonCancelTabletStyle}
                                    title={t('COMMON.BTN_CANCEL')}
                                    isSecondary
                                    onPress={close}
                                />
                                <Button
                                    style={buttonTabletStyle}
                                    title={t(continueButtonText)}
                                    onPress={onContinuePress}
                                    isDisabled={!isButtonEnabled}
                                    isSecondary={!isButtonEnabled}
                                />
                            </View>
                        </View>
                    </ScrollView>
                )}
            </SafeAreaView>
        </ImageBackground>
    );
};

export default VoyoConsentDialog;
